/**
 * Cookie consent banner (DSGVO / TTDSG).
 *
 * Consent is written to Shopify's Customer Privacy API. That API - not this
 * script - is what actually gates the pixels registered under
 * Settings > Customer events, so any pixel added there later is covered
 * without touching this file. Scripts injected through the legacy ScriptTag
 * API (currently Avada and UpPromote) are outside that mechanism and cannot
 * be blocked from the theme.
 */

const STORAGE_KEY = 'ff_cookie_consent';
const CONSENT_VERSION = 1;

const CATEGORIES = ['preferences', 'analytics', 'marketing'];

const selectors = {
  root: '[data-cookie-consent]',
  banner: '[data-cookie-consent-banner]',
  details: '[data-cookie-consent-details]',
  openDetails: '[data-cookie-consent-open-details]',
  acceptAll: '[data-cookie-consent-accept-all]',
  rejectAll: '[data-cookie-consent-reject-all]',
  saveSelection: '[data-cookie-consent-save]',
  reopen: '[data-cookie-consent-reopen]',
  checkbox: '[data-cookie-consent-category]',
};

/**
 * Reads the stored consent record.
 * @returns {{version: number, timestamp: string, categories: Record<string, boolean>} | null}
 */
function readStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, boolean>} categories
 */
function storeConsent(categories) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: CONSENT_VERSION, timestamp: new Date().toISOString(), categories })
    );
  } catch {
    // Storage can be unavailable (private mode). Shopify's own consent cookie
    // remains the authoritative record, so this is not fatal.
  }
}

/**
 * Meta's pixel ships no consent logic of its own: it loads unconditionally
 * and would track as soon as anything calls fbq('track'). Its own consent
 * API holds those events in a queue until they are granted.
 * @param {Record<string, boolean>} categories
 */
function signalMetaPixel(categories) {
  const fbq = /** @type {any} */ (window).fbq;
  if (typeof fbq !== 'function') return;

  fbq('consent', categories.marketing ? 'grant' : 'revoke');
}

let clarityRequested = false;

/**
 * Clarity is loaded from here rather than by its Shopify app, so that
 * nothing of it reaches the page until statistics consent exists. The app
 * version loads on every page view and only afterwards reports the consent
 * state, which still sends data for visitors who declined.
 * @param {string} projectId
 */
function loadClarity(projectId) {
  if (clarityRequested || !projectId) return;
  clarityRequested = true;

  const w = /** @type {any} */ (window);
  w.clarity =
    w.clarity ||
    function () {
      (w.clarity.q = w.clarity.q || []).push(arguments);
    };

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;
  document.head.appendChild(script);
}

/**
 * @param {Record<string, boolean>} categories
 * @param {string} projectId
 */
function applyClarity(categories, projectId) {
  if (categories.analytics) {
    loadClarity(projectId);
    return;
  }

  // Withdrawn mid-session: the script cannot be unloaded, so tell it to drop
  // to its consentless mode. The next page load leaves it out entirely.
  const clarity = /** @type {any} */ (window).clarity;
  if (typeof clarity === 'function') {
    clarity('consentv2', { ad_Storage: 'denied', analytics_Storage: 'denied' });
  }
}

/**
 * Shopify's script that defines `loadFeatures` arrives asynchronously, so a
 * decision made before it lands would otherwise be dropped silently.
 * @returns {Promise<any>} resolves with the Shopify global, or null on timeout
 */
function shopifyReady(timeoutMs = 10000) {
  return new Promise((resolve) => {
    const shopify = /** @type {any} */ (window).Shopify;
    if (shopify?.loadFeatures) return resolve(shopify);

    const startedAt = Date.now();
    const poll = setInterval(() => {
      const candidate = /** @type {any} */ (window).Shopify;
      if (candidate?.loadFeatures) {
        clearInterval(poll);
        resolve(candidate);
      } else if (Date.now() - startedAt > timeoutMs) {
        clearInterval(poll);
        resolve(null);
      }
    }, 100);
  });
}

/**
 * Hands the decision to Shopify, which propagates it to every pixel
 * registered through Customer events.
 * @param {Record<string, boolean>} categories
 * @param {string} clarityProjectId
 */
async function applyConsent(categories, clarityProjectId) {
  signalMetaPixel(categories);
  applyClarity(categories, clarityProjectId);

  const shopify = await shopifyReady();
  if (!shopify) return;

  shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
    if (error) return;

    shopify.customerPrivacy?.setTrackingConsent(
      {
        preferences: categories.preferences,
        analytics: categories.analytics,
        marketing: categories.marketing,
        // Conservative default: treat marketing consent as the signal for
        // data sharing so the CCPA/CPRA flag never exceeds what the visitor
        // agreed to under GDPR.
        sale_of_data: categories.marketing,
      },
      () => {}
    );
  });
}

class CookieConsent {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.banner = /** @type {HTMLElement} */ (root.querySelector(selectors.banner));
    this.details = /** @type {HTMLElement} */ (root.querySelector(selectors.details));
    this.clarityProjectId = root.dataset.clarityProjectId ?? '';

    this.#bindEvents();

    const stored = readStoredConsent();
    if (stored) {
      applyConsent(stored.categories, this.clarityProjectId);
    } else {
      // No decision yet means denied, so the pixel has to be told before the
      // visitor ever reaches the buttons. Clarity simply stays unloaded.
      signalMetaPixel({ preferences: false, analytics: false, marketing: false });
      this.open();
    }
  }

  #bindEvents() {
    // Accept and reject appear in both views, so every match has to be bound.
    this.#bindAll(selectors.openDetails, () => this.#showDetails());
    this.#bindAll(selectors.acceptAll, () => this.#decideAll(true));
    this.#bindAll(selectors.rejectAll, () => this.#decideAll(false));
    this.#bindAll(selectors.saveSelection, () => this.#saveSelection());

    for (const trigger of document.querySelectorAll(selectors.reopen)) {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    }
  }

  /**
   * @param {string} selector
   * @param {() => void} handler
   */
  #bindAll(selector, handler) {
    for (const element of this.root.querySelectorAll(selector)) {
      element.addEventListener('click', handler);
    }
  }

  open() {
    const stored = readStoredConsent();
    for (const checkbox of this.#checkboxes()) {
      checkbox.checked = stored ? Boolean(stored.categories[checkbox.value]) : false;
    }

    this.root.hidden = false;
    this.details.hidden = true;
    this.banner.hidden = false;
    this.root.querySelector(selectors.acceptAll)?.focus();
  }

  close() {
    this.root.hidden = true;
  }

  #showDetails() {
    this.banner.hidden = true;
    this.details.hidden = false;
  }

  /** @returns {HTMLInputElement[]} */
  #checkboxes() {
    return Array.from(this.root.querySelectorAll(selectors.checkbox));
  }

  /** @param {boolean} value */
  #decideAll(value) {
    this.#commit(Object.fromEntries(CATEGORIES.map((category) => [category, value])));
  }

  #saveSelection() {
    const selected = Object.fromEntries(CATEGORIES.map((category) => [category, false]));
    for (const checkbox of this.#checkboxes()) {
      if (checkbox.checked) selected[checkbox.value] = true;
    }
    this.#commit(selected);
  }

  /** @param {Record<string, boolean>} categories */
  #commit(categories) {
    storeConsent(categories);
    applyConsent(categories, this.clarityProjectId);
    this.close();
  }
}

const root = document.querySelector(selectors.root);
if (root instanceof HTMLElement) {
  new CookieConsent(root);
}
